import React, { useEffect, useState } from 'react';
import { Save, RotateCcw, FileText, Loader2, Eye, FileDown, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'deck_title',    label: 'PDF Title',            type: 'text',     hint: 'Top of the stage page (e.g. "Your Brief Is Confirmed")' },
  { key: 'deck_tagline',  label: 'PDF Tagline',          type: 'text',     hint: 'One-liner under the title (gold italic)' },
  { key: 'deck_body',     label: 'PDF Body Copy',        type: 'textarea', hint: 'The long-form paragraph on the stage page' },
  { key: 'deck_delivery', label: 'PDF "What you receive"', type: 'text',   hint: 'Bottom card on the stage page' },
  { key: 'headline',      label: 'Notification Headline', type: 'text',    hint: 'Email/WhatsApp/Telegram headline' },
  { key: 'subject',       label: 'Email Subject',         type: 'text',    hint: 'Subject line of the investor email' },
  { key: 'body',          label: 'Notification Body',     type: 'textarea', hint: 'Email/WhatsApp/Telegram body' },
];

export const JourneyContentTab = ({ getStoredPassword }) => {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/journey-content?password=${getStoredPassword()}`);
      if (r.ok) {
        const d = await r.json();
        setData(d);
        setEdits({});
      } else toast.error('Failed to load journey content');
    } catch { toast.error('Network error'); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setField = (stageKey, field, value) => {
    setEdits(prev => ({
      ...prev,
      [stageKey]: { ...(prev[stageKey] || {}), [field]: value },
    }));
  };

  const getValue = (stageKey, field) => {
    const e = edits[stageKey] || {};
    if (field in e) return e[field];
    return data?.stages?.[stageKey]?.[field] || '';
  };

  const isDirty = (stageKey, field) => {
    const e = edits[stageKey] || {};
    if (!(field in e)) return false;
    return e[field] !== (data?.stages?.[stageKey]?.[field] || '');
  };

  const dirtyCount = Object.values(edits).reduce(
    (sum, e) => sum + Object.keys(e).length, 0
  );

  const save = async () => {
    if (dirtyCount === 0) {
      toast.info('No changes to save');
      return;
    }
    setSaving(true);
    // Build full payload: each stage in edits + merge with existing override (so unchanged custom values persist)
    const stages = {};
    for (const [k, v] of Object.entries(edits)) {
      stages[k] = v;
    }
    try {
      const r = await fetch(`${API_URL}/api/admin/journey-content?password=${getStoredPassword()}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stages }),
      });
      if (r.ok) {
        toast.success(`Saved ${dirtyCount} override${dirtyCount !== 1 ? 's' : ''}`);
        await load();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Save failed');
      }
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const reset = async () => {
    if (!window.confirm('Revert ALL stages to built-in defaults? Any custom copy will be lost.')) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/journey-content/reset?password=${getStoredPassword()}`, { method: 'POST' });
      if (r.ok) {
        toast.success('Reverted to defaults');
        await load();
      } else toast.error('Reset failed');
    } catch { toast.error('Network error'); }
  };

  if (loading || !data) {
    return <div className="text-white/50 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  const orderedKeys = data.deck_stage_keys || ['brief_confirmed', 'deposit_paid', 'sourcing', 'presented', 'won'];

  return (
    <div className="space-y-6" data-testid="journey-content-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-white text-2xl font-bold tracking-tight">Client Journey Deck Copy</h2>
          <p className="text-white/50 text-sm mt-1 max-w-2xl">
            Edit the wording that appears in every investor's 7-page PDF deck <strong>and</strong> in every stage-change email / WhatsApp / Telegram notification.
            Empty fields fall back to the built-in defaults shown as placeholders.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={reset}
            variant="outline"
            className="border-red-500/30 text-red-300 hover:bg-red-500/10"
            data-testid="journey-content-reset"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" /> Revert all to defaults
          </Button>
          <Button
            onClick={save}
            disabled={saving || dirtyCount === 0}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941E] disabled:opacity-40"
            data-testid="journey-content-save"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Saving…' : `Save${dirtyCount > 0 ? ` (${dirtyCount})` : ''}`}
          </Button>
        </div>
      </div>

      {orderedKeys.map((stageKey, idx) => (
        <div
          key={stageKey}
          className="bg-white/[0.02] border border-white/10 rounded-xl overflow-hidden"
          data-testid={`journey-stage-${stageKey}`}
        >
          <div className="bg-[#0a1a14] border-b border-[#D4AF37]/15 px-5 py-3 flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold">Stage {idx + 1} of 5</span>
            <span className="text-white/30 text-xs">·</span>
            <span className="text-white font-mono text-sm">{stageKey}</span>
            <a
              href={`${API_URL}/api/admin/bespoke-requests/preview/journey-deck.pdf?password=${getStoredPassword()}`}
              className="ml-auto text-white/30 text-[11px] flex items-center gap-1 pointer-events-none"
              aria-disabled
            >
              <FileText className="w-3 h-3" /> appears in PDF + notifications
            </a>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.map(f => {
              const value = getValue(stageKey, f.key);
              const placeholder = data.defaults?.[stageKey]?.[f.key] || '';
              const dirty = isDirty(stageKey, f.key);
              const isLong = f.type === 'textarea';
              return (
                <div key={f.key} className={isLong ? 'md:col-span-2' : ''}>
                  <label className="text-[10px] uppercase tracking-widest text-white/50 font-semibold flex items-center gap-2">
                    {f.label}
                    {dirty && <span className="bg-[#D4AF37] text-black text-[8px] px-1.5 py-0.5 rounded font-bold">EDITED</span>}
                  </label>
                  <p className="text-white/30 text-[10px] mb-1.5">{f.hint}</p>
                  {isLong ? (
                    <textarea
                      value={value}
                      onChange={e => setField(stageKey, f.key, e.target.value)}
                      placeholder={placeholder}
                      rows={4}
                      className="w-full bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40 resize-y"
                      data-testid={`journey-${stageKey}-${f.key}`}
                    />
                  ) : (
                    <input
                      value={value}
                      onChange={e => setField(stageKey, f.key, e.target.value)}
                      placeholder={placeholder}
                      className="w-full bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
                      data-testid={`journey-${stageKey}-${f.key}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* ============== Live Preview ============== */}
      <JourneyPreview getStoredPassword={getStoredPassword} dirtyCount={dirtyCount} />
    </div>
  );
};


// ============== Live Preview ==============
const JourneyPreview = ({ getStoredPassword, dirtyCount }) => {
  const [variant, setVariant] = useState('unsigned'); // 'unsigned' | 'signed'
  const [page, setPage] = useState(1);
  const [pngUrl, setPngUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errPage, setErrPage] = useState(false);
  // Reset to page 1 when variant changes
  useEffect(() => { setPage(1); }, [variant]);
  const totalPages = variant === 'signed' ? 8 : 7;

  const loadPage = async () => {
    setLoading(true);
    setErrPage(false);
    try {
      const url = `${API_URL}/api/admin/journey-content/preview.png?password=${encodeURIComponent(getStoredPassword())}&page=${page}&signed=${variant === 'signed'}&scale=1.6&_=${Date.now()}`;
      const r = await fetch(url);
      if (!r.ok) {
        setErrPage(true);
        return;
      }
      const blob = await r.blob();
      // Revoke old URL to free memory
      if (pngUrl) URL.revokeObjectURL(pngUrl);
      setPngUrl(URL.createObjectURL(blob));
    } catch {
      setErrPage(true);
    }
    setLoading(false);
  };

  useEffect(() => { loadPage(); /* eslint-disable-next-line */ }, [page, variant]);
  useEffect(() => () => { if (pngUrl) URL.revokeObjectURL(pngUrl); }, [pngUrl]);

  const downloadPdf = () => {
    const url = `${API_URL}/api/admin/journey-content/preview.pdf?password=${encodeURIComponent(getStoredPassword())}&signed=${variant === 'signed'}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const downloadPng = () => {
    if (!pngUrl) return;
    const a = document.createElement('a');
    a.href = pngUrl;
    a.download = `journey-deck-${variant}-page${page}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="bg-white/[0.02] border border-[#D4AF37]/20 rounded-xl overflow-hidden" data-testid="journey-preview">
      <div className="bg-[#0a1a14] border-b border-[#D4AF37]/15 px-5 py-3 flex items-center gap-3 flex-wrap">
        <Eye className="w-4 h-4 text-[#D4AF37]" />
        <h3 className="text-white text-sm font-bold">Live Preview</h3>
        <span className="text-white/40 text-[11px]">Renders with the latest <strong>saved</strong> content using a sample investor.</span>
        {dirtyCount > 0 && (
          <span className="text-yellow-300 text-[10px] bg-yellow-400/10 border border-yellow-400/30 px-2 py-0.5 rounded font-semibold uppercase tracking-widest">Save to refresh preview</span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <div className="flex rounded-md overflow-hidden border border-white/10" data-testid="journey-preview-variant">
            <button
              onClick={() => setVariant('unsigned')}
              className={`text-[11px] px-3 py-1.5 transition-colors ${variant === 'unsigned' ? 'bg-[#D4AF37] text-black font-semibold' : 'bg-transparent text-white/60 hover:text-white'}`}
              data-testid="journey-preview-variant-unsigned"
            >
              7 pages (unsigned)
            </button>
            <button
              onClick={() => setVariant('signed')}
              className={`text-[11px] px-3 py-1.5 transition-colors ${variant === 'signed' ? 'bg-[#D4AF37] text-black font-semibold' : 'bg-transparent text-white/60 hover:text-white'}`}
              data-testid="journey-preview-variant-signed"
            >
              8 pages (signed)
            </button>
          </div>
        </div>
      </div>

      <div className="p-5 flex flex-col lg:flex-row gap-5">
        {/* Page viewer */}
        <div className="flex-1 min-h-[500px] bg-[#070f0c] rounded-md flex items-center justify-center relative overflow-hidden border border-white/5">
          {loading && <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />}
          {!loading && errPage && (
            <div className="text-center text-white/40 text-xs p-6">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              Failed to render page. Try saving any pending edits first.
            </div>
          )}
          {!loading && !errPage && pngUrl && (
            <img
              src={pngUrl}
              alt={`Journey deck preview page ${page}`}
              className="max-w-full max-h-[720px] shadow-2xl"
              data-testid="journey-preview-image"
            />
          )}
        </div>

        {/* Sidebar controls */}
        <div className="lg:w-56 flex flex-row lg:flex-col gap-2.5">
          <div className="flex-1 lg:flex-none flex items-center justify-between gap-2 bg-[#0a1a14] border border-white/10 rounded-md px-3 py-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="p-1 rounded text-white/60 hover:text-white disabled:opacity-30"
              data-testid="journey-preview-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-white text-sm font-semibold tabular-nums" data-testid="journey-preview-pagenum">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="p-1 rounded text-white/60 hover:text-white disabled:opacity-30"
              data-testid="journey-preview-next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <Button
            onClick={downloadPdf}
            variant="outline"
            size="sm"
            className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-9 text-xs"
            data-testid="journey-preview-download-pdf"
          >
            <FileDown className="w-3.5 h-3.5 mr-1.5" /> Download PDF
          </Button>
          <Button
            onClick={downloadPng}
            variant="outline"
            size="sm"
            disabled={!pngUrl || loading}
            className="border-white/15 text-white/70 hover:bg-white/5 h-9 text-xs disabled:opacity-40"
            data-testid="journey-preview-download-png"
          >
            <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Download Page PNG
          </Button>
          <p className="text-white/30 text-[10px] leading-relaxed mt-1 hidden lg:block">
            <strong className="text-white/50">Tip:</strong> Save your edits first — the preview always uses the last saved version.
          </p>
        </div>
      </div>
    </div>
  );
};
